import React, { useState, useRef } from 'react';
import { authService } from './services/authService';
import JSZip from 'jszip';

const EpicFromFileForm = ({ selectedProject, selectedModel, userInfo }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [epicName, setEpicName] = useState('');
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState(1); // 1: Upload, 2: Review, 3: Confirm Create
  const fileInputRef = useRef(null);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setLoading(true);

    try {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let content = '';

      // Read file content
      if (fileExtension === 'txt') {
        content = await file.text();
      } else if (fileExtension === 'csv') {
        content = await file.text();
      } else if (fileExtension === 'docx') {
        // For docx files, we'll need to parse them differently
        // For now, we'll use a simple approach
        const buffer = await file.arrayBuffer();
        // Basic DOCX parsing - extract text
        content = await parseDocx(buffer);
      } else if (fileExtension === 'json') {
        content = await file.text();
      } else {
        throw new Error(`Unsupported file format: ${fileExtension}`);
      }

      // Parse the content to extract epic name and stories
      const parsed = parseFileContent(content, fileExtension);
      setUploadedFile(file);
      
      // Use AI to analyze and intelligently fill the form
      await analyzeAndFillForm(content, parsed);
      setStep(2);
    } catch (err) {
      setError(`Error parsing file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const analyzeAndFillForm = async (content, parsed) => {
    try {
      const token = await authService.getAccessToken();
      
      // Send content to AI for analysis
      const response = await fetch('/api/ai/ask', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: `Analyze this document and determine if it describes an EPIC or individual STORY/Task.

Document Content (first 500 chars):
${content.substring(0, 500)}

Respond with ONLY valid JSON (no markdown, no code blocks):
{
  "type": "epic" OR "story",
  "reasoning": "brief explanation",
  "suggestedTitle": "suggested title based on content",
  "suggestedDescription": "2-3 sentence summary"
}`,
          selectedModel: selectedModel || 'gemini-2.5-flash',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        let analysis = { type: 'epic', suggestedTitle: '', suggestedDescription: '' };
        
        try {
          const jsonMatch = result.response.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            analysis = JSON.parse(jsonMatch[0]);
          }
        } catch (parseErr) {
          console.warn('Could not parse AI response:', parseErr);
        }

        // Fill form based on analysis
        if (analysis.type === 'story') {
          // For a story, create a single item in the stories array
          const singleStory = {
            title: analysis.suggestedTitle || parsed.epicName || 'Story from file',
            description: analysis.suggestedDescription || '',
            acceptanceCriteria: '',
          };
          setEpicName(parsed.epicName || 'User Story');
          setStories([singleStory]);
        } else {
          // For an epic, use the parsed data
          setEpicName(parsed.epicName || analysis.suggestedTitle || 'Epic from file');
          setStories(parsed.stories.length > 0 ? parsed.stories : [
            {
              title: 'Story 1',
              description: analysis.suggestedDescription || '',
              acceptanceCriteria: '',
            }
          ]);
        }
      } else {
        // Fallback if AI fails
        setEpicName(parsed.epicName || '');
        setStories(parsed.stories || []);
      }
    } catch (err) {
      console.warn('AI analysis failed, using basic parsing:', err);
      setEpicName(parsed.epicName || '');
      setStories(parsed.stories || []);
    }
  };

  const parseFileContent = (content, fileType) => {
    let epicName = '';
    let stories = [];

    if (fileType === 'csv') {
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length > 0) {
        // First line is epic name
        epicName = lines[0].trim();
        // Remaining lines are stories
        for (let i = 1; i < lines.length; i++) {
          const parts = lines[i].split(',');
          if (parts.length > 0) {
            stories.push({
              title: parts[0].trim(),
              description: parts.length > 1 ? parts[1].trim() : '',
              acceptanceCriteria: parts.length > 2 ? parts[2].trim() : '',
            });
          }
        }
      }
    } else if (fileType === 'json') {
      const parsed = JSON.parse(content);
      epicName = parsed.epicName || parsed.name || 'New Epic';
      stories = parsed.stories || parsed.items || [];
    } else {
      // Plain text format
      const lines = content.split('\n').filter(line => line.trim());
      if (lines.length > 0) {
        epicName = lines[0].trim();
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line && !line.startsWith('#')) {
            stories.push({
              title: line,
              description: '',
              acceptanceCriteria: '',
            });
          }
        }
      }
    }

    return { epicName, stories };
  };

  const parseDocx = async (arrayBuffer) => {
    try {
      // Use JSZip to read the DOCX file
      const zip = new JSZip();
      await zip.loadAsync(arrayBuffer);
      
      // Read the document.xml file from the DOCX
      const xmlFile = zip.file('word/document.xml');
      if (!xmlFile) {
        throw new Error('Invalid DOCX file structure');
      }
      
      const xmlContent = await xmlFile.async('string');
      
      // Extract text from <w:t> tags
      const textMatches = xmlContent.match(/<w:t[^>]*>([^<]*)<\/w:t>/g) || [];
      const text = textMatches
        .map(match => match.replace(/<w:t[^>]*>|<\/w:t>/g, ''))
        .join('\n')
        .trim();
      
      if (!text) {
        throw new Error('No text found in DOCX file');
      }
      
      return text;
    } catch (err) {
      console.error('DOCX parsing error:', err);
      // Fallback: try to extract raw text from binary
      try {
        const decoder = new TextDecoder('utf-8', { fatal: false });
        const text = decoder.decode(arrayBuffer);
        const cleanText = text.replace(/[^\x20-\x7E\n\r\t]/g, '').trim();
        if (cleanText.length > 10) {
          return cleanText;
        }
      } catch (fallbackErr) {
        console.error('Fallback parsing failed:', fallbackErr);
      }
      throw new Error('Could not parse DOCX file. Please ensure it\'s a valid Word document.');
    }
  };

  const handleCreateEpic = async () => {
    if (!selectedProject) {
      setError('Please select a project first');
      return;
    }

    if (!epicName.trim()) {
      setError('Epic name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = await authService.getAccessToken();
      
      // First, create the epic
      const epicResponse = await fetch('/api/jira/create-epic-from-file', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectKey: selectedProject,
          epicName: epicName,
          description: `Created from file: ${uploadedFile?.name || 'Unknown'}`,
          stories: stories,
          selectedModel: selectedModel,
          userEmail: userInfo?.email,
          userName: userInfo?.name,
        }),
      });

      if (!epicResponse.ok) {
        const errorData = await epicResponse.json();
        throw new Error(errorData.message || 'Failed to create epic');
      }

      await epicResponse.json();
      
      setSuccess(`✅ Epic "${epicName}" created successfully with ${stories.length} stories ready for creation!`);
      setStep(3);
      
      // Reset form after 2 seconds
      setTimeout(() => {
        resetForm();
      }, 2000);
    } catch (err) {
      setError(`Error creating epic: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUploadedFile(null);
    setEpicName('');
    setStories([]);
    setStep(1);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateStory = (index, field, value) => {
    const newStories = [...stories];
    newStories[index] = { ...newStories[index], [field]: value };
    setStories(newStories);
  };

  const removeStory = (index) => {
    const newStories = stories.filter((_, i) => i !== index);
    setStories(newStories);
  };

  const addStory = () => {
    setStories([...stories, { title: '', description: '', acceptanceCriteria: '' }]);
  };

  if (!selectedProject) {
    return (
      <div style={styles.container}>
        <div style={styles.noProjectMessage}>
          ⚠️ Please select a project from the Project Selector above to create an Epic from file.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📂 Create Epic from File</h2>
        <p style={styles.subtitle}>Upload a file (docx, CSV, or text) to create an Epic with Stories</p>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}
      {success && <div style={styles.successAlert}>{success}</div>}

      {/* Step 1: Upload File */}
      {step === 1 && (
        <div style={styles.stepContainer}>
          <h3>Step 1: Upload Your File</h3>
          <p style={styles.helpText}>
            Supported formats: <strong>DOCX</strong>, <strong>CSV</strong>, <strong>TXT</strong>, <strong>JSON</strong>
          </p>
          
          <div style={styles.uploadArea}>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileUpload}
              accept=".docx,.csv,.txt,.json"
              disabled={loading}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              style={styles.uploadButton}
            >
              {loading ? '⏳ Processing...' : '📤 Choose File'}
            </button>
            <p style={styles.uploadHint}>
              {uploadedFile 
                ? `✅ File selected: ${uploadedFile.name}` 
                : 'Click to select a file or drag and drop'}
            </p>
          </div>

          <div style={styles.formatGuide}>
            <h4>📋 File Format Guide</h4>
            <details>
              <summary><strong>Plain Text (.txt)</strong></summary>
              <pre style={styles.codeBlock}>
Epic Name
Story 1 Title
Story 2 Title
Story 3 Title
              </pre>
            </details>

            <details>
              <summary><strong>CSV (.csv)</strong></summary>
              <pre style={styles.codeBlock}>
Epic Name
Story 1, Description here, Acceptance criteria
Story 2, Description here, Acceptance criteria
              </pre>
            </details>

            <details>
              <summary><strong>JSON (.json)</strong></summary>
              <pre style={styles.codeBlock}>
{`{
  "epicName": "My Epic",
  "stories": [
    { "title": "Story 1", "description": "...", "acceptanceCriteria": "..." },
    { "title": "Story 2", "description": "...", "acceptanceCriteria": "..." }
  ]
}`}
              </pre>
            </details>
          </div>
        </div>
      )}

      {/* Step 2: Review & Edit */}
      {step === 2 && (
        <div style={styles.stepContainer}>
          <h3>Step 2: Review & Edit Epic Details</h3>

          <div style={styles.formGroup}>
            <label style={styles.label}>Epic Name:</label>
            <input
              type="text"
              value={epicName}
              onChange={(e) => setEpicName(e.target.value)}
              style={styles.input}
              placeholder="Enter epic name..."
            />
          </div>

          <div style={styles.storiesContainer}>
            <h4 style={{ marginTop: 0 }}>Stories ({stories.length})</h4>
            
            {stories.map((story, idx) => (
              <div key={idx} style={styles.storyCard}>
                <button
                  onClick={() => removeStory(idx)}
                  style={styles.removeButton}
                  title="Remove story"
                >
                  ✕
                </button>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Title:</label>
                  <input
                    type="text"
                    value={story.title}
                    onChange={(e) => updateStory(idx, 'title', e.target.value)}
                    style={styles.input}
                    placeholder="Story title..."
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Description:</label>
                  <textarea
                    value={story.description}
                    onChange={(e) => updateStory(idx, 'description', e.target.value)}
                    style={styles.textarea}
                    placeholder="Story description..."
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Acceptance Criteria:</label>
                  <textarea
                    value={story.acceptanceCriteria}
                    onChange={(e) => updateStory(idx, 'acceptanceCriteria', e.target.value)}
                    style={styles.textarea}
                    placeholder="Acceptance criteria..."
                  />
                </div>
              </div>
            ))}

            <button
              onClick={addStory}
              style={styles.addStoryButton}
            >
              ➕ Add Story
            </button>
          </div>

          <div style={styles.buttonGroup}>
            <button
              onClick={() => setStep(1)}
              style={styles.backButton}
            >
              ← Back
            </button>
            <button
              onClick={() => setStep(3)}
              style={styles.nextButton}
              disabled={!epicName.trim() || stories.length === 0}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm & Create */}
      {step === 3 && (
        <div style={styles.stepContainer}>
          <h3>Step 3: Confirm & Create Epic</h3>
          
          <div style={styles.confirmBox}>
            <h4>📋 Epic Summary</h4>
            <p><strong>Epic Name:</strong> {epicName}</p>
            <p><strong>Project:</strong> {selectedProject}</p>
            <p><strong>Stories:</strong> {stories.length}</p>
          </div>

          <div style={styles.storiesSummary}>
            <h4>Stories to be created:</h4>
            <ul>
              {stories.map((story, idx) => (
                <li key={idx} style={{ marginBottom: '0.5rem' }}>
                  <strong>{story.title}</strong>
                  {story.description && <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                    {story.description.substring(0, 100)}...
                  </p>}
                </li>
              ))}
            </ul>
          </div>

          <div style={styles.buttonGroup}>
            <button
              onClick={() => setStep(2)}
              style={styles.backButton}
            >
              ← Back
            </button>
            <button
              onClick={handleCreateEpic}
              style={styles.createButton}
              disabled={loading}
            >
              {loading ? '⏳ Creating...' : '✨ Create Epic'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    maxWidth: '1000px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '2rem',
  },
  title: {
    fontSize: '1.8rem',
    fontWeight: '700',
    margin: 0,
    color: '#0052cc',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    marginTop: '0.5rem',
  },
  stepContainer: {
    marginBottom: '2rem',
  },
  noProjectMessage: {
    padding: '1rem',
    backgroundColor: '#fff3e0',
    border: '1px solid #ff9800',
    borderRadius: '4px',
    color: '#ff9800',
    fontWeight: 'bold',
  },
  errorAlert: {
    padding: '1rem',
    backgroundColor: '#ffebee',
    border: '1px solid #f44336',
    borderRadius: '4px',
    color: '#c62828',
    marginBottom: '1rem',
  },
  successAlert: {
    padding: '1rem',
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
    borderRadius: '4px',
    color: '#2e7d32',
    marginBottom: '1rem',
  },
  uploadArea: {
    border: '2px dashed #ccc',
    borderRadius: '8px',
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    marginBottom: '1.5rem',
  },
  uploadButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#0052cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  uploadHint: {
    color: '#999',
    fontSize: '0.9rem',
    marginTop: '1rem',
  },
  helpText: {
    color: '#666',
    marginBottom: '1rem',
  },
  formatGuide: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '1rem',
    marginTop: '1rem',
  },
  codeBlock: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '0.75rem',
    fontSize: '0.85rem',
    overflow: 'auto',
    marginTop: '0.5rem',
  },
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '0.5rem',
    color: '#333',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '1rem',
    minHeight: '80px',
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  storiesContainer: {
    marginBottom: '1.5rem',
  },
  storyCard: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: '#f9f9f9',
    position: 'relative',
  },
  removeButton: {
    position: 'absolute',
    top: '0.5rem',
    right: '0.5rem',
    background: 'none',
    border: 'none',
    fontSize: '1.2rem',
    cursor: 'pointer',
    color: '#f44336',
  },
  addStoryButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'center',
    marginTop: '2rem',
  },
  backButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#9e9e9e',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  nextButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#0052cc',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  createButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  confirmBox: {
    border: '1px solid #0052cc',
    borderRadius: '4px',
    padding: '1rem',
    backgroundColor: '#f0f4ff',
    marginBottom: '1.5rem',
  },
  storiesSummary: {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '1rem',
    marginBottom: '1.5rem',
  },
};

export default EpicFromFileForm;
